import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Input } from '@mono/ui-components';
import { ChevronRight, FileSpreadsheet, FileText, FolderOpen, FolderPlus, MoreVertical, Presentation, Search, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Breadcrumbs } from './components/Breadcrumbs';
import { ConfirmDialog } from './components/ConfirmDialog';
import { DocumentRowSkeleton, FolderGridSkeleton } from './components/DocumentRowSkeleton';
import { MoveDialog } from './components/MoveDialog';
import { PromptDialog } from './components/PromptDialog';
import { useDebouncedValue } from './hooks/useDebouncedValue';
import { useCreateDocument, useDeleteDocument, useDocuments, useMoveDocument, useRenameDocument } from './hooks/useDocuments';
import { useCreateFolder, useDeleteFolder, useFolders, useRenameFolder } from './hooks/useFolders';
import { generateBlankTemplate } from './lib/blankTemplate';
import { relativeTime } from './lib/time';
const DOC_TYPE_FROM_EXT = {
    docx: 'docx',
    xlsx: 'xlsx',
    pptx: 'pptx'
};
function formatBytes(n) {
    if (n === 0) return '0 B';
    const units = [
        'B',
        'KB',
        'MB',
        'GB'
    ];
    const i = Math.floor(Math.log(n) / Math.log(1024));
    return `${(n / 1024 ** i).toFixed(1)} ${units[i]}`;
}
function typeIcon(t) {
    switch(t){
        case 'xlsx':
            return /*#__PURE__*/ _jsx(FileSpreadsheet, {
                className: "w-5 h-5 text-green-600"
            });
        case 'pptx':
            return /*#__PURE__*/ _jsx(Presentation, {
                className: "w-5 h-5 text-orange-500"
            });
        default:
            return /*#__PURE__*/ _jsx(FileText, {
                className: "w-5 h-5 text-blue-500"
            });
    }
}
/**
 * EditorList renders the /editor route: folders, documents, and their actions.
 * Everything interactive uses Dialogs + toasts. Real breadcrumbs resolve the
 * full parent chain via getFolder RPC.
 */ export default function EditorList() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const uploadInputRef = useRef(null);
    const [searchInput, setSearchInput] = useState('');
    const search = useDebouncedValue(searchInput, 250);
    const { data: docData, isLoading: docsLoading, isFetching: docsFetching } = useDocuments({
        folderId,
        search
    });
    const { data: folders, isLoading: foldersLoading } = useFolders(folderId);
    const createFolder = useCreateFolder();
    const renameFolder = useRenameFolder();
    const deleteFolder = useDeleteFolder();
    const createDoc = useCreateDocument();
    const renameDoc = useRenameDocument();
    const moveDoc = useMoveDocument();
    const deleteDoc = useDeleteDocument();
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [renameFolderTarget, setRenameFolderTarget] = useState(null);
    const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const [moveTarget, setMoveTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    async function handleCreateBlank(type) {
        try {
            const bytes = await generateBlankTemplate(type);
            const doc = await createDoc.mutateAsync({
                name: `Untitled.${type}`,
                documentType: type,
                folderId,
                initialBytes: bytes
            });
            toast.success(`Created ${doc.name}`);
            navigate(`/editor/${doc.id}`);
        } catch (err) {
            toast.error(`Couldn't create document: ${err.message}`);
        }
    }
    async function handleUpload(e) {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const type = DOC_TYPE_FROM_EXT[ext];
        if (!type) {
            toast.error(`Unsupported file type (.${ext}). Use .docx, .xlsx, or .pptx.`);
            return;
        }
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            const doc = await createDoc.mutateAsync({
                name: file.name,
                documentType: type,
                folderId,
                initialBytes: bytes
            });
            toast.success(`Uploaded ${doc.name}`);
            navigate(`/editor/${doc.id}`);
        } catch (err) {
            toast.error(`Upload failed: ${err.message}`);
        }
    }
    const noResults = !docsLoading && docData && docData.documents.length === 0;
    return /*#__PURE__*/ _jsxs("div", {
        className: "p-6 max-w-6xl mx-auto",
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: "Editor"
                })
            }),
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-start justify-between mb-6 gap-4 flex-wrap",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-2xl font-semibold",
                                children: "Documents"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground mt-1",
                                children: "Real-time collaborative editing for .docx, .xlsx, and .pptx."
                            }),
                            /*#__PURE__*/ _jsx(Breadcrumbs, {
                                folderId: folderId
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 shrink-0",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setNewFolderOpen(true),
                                className: "inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-muted",
                                children: [
                                    /*#__PURE__*/ _jsx(FolderPlus, {
                                        className: "w-4 h-4"
                                    }),
                                    "New folder"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>uploadInputRef.current?.click(),
                                className: "inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90",
                                children: [
                                    /*#__PURE__*/ _jsx(Upload, {
                                        className: "w-4 h-4"
                                    }),
                                    "Upload"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                ref: uploadInputRef,
                                type: "file",
                                className: "hidden",
                                accept: ".docx,.xlsx,.pptx",
                                onChange: handleUpload
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("section", {
                className: "flex gap-2 mb-6 flex-wrap",
                children: [
                    'docx',
                    'xlsx',
                    'pptx'
                ].map((t)=>/*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        disabled: createDoc.isPending,
                        onClick: ()=>handleCreateBlank(t),
                        className: "inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm disabled:opacity-50 hover:bg-muted",
                        children: [
                            typeIcon(t),
                            "New ",
                            t
                        ]
                    }, t))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-6 relative max-w-md",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        value: searchInput,
                        onChange: (e)=>setSearchInput(e.target.value),
                        placeholder: "Search documents…",
                        className: "pl-9",
                        "aria-label": "Search documents"
                    })
                ]
            }),
            foldersLoading ? /*#__PURE__*/ _jsxs("section", {
                className: "mb-8",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-xs uppercase tracking-wide text-muted-foreground mb-2",
                        children: "Folders"
                    }),
                    /*#__PURE__*/ _jsx(FolderGridSkeleton, {})
                ]
            }) : folders && folders.length > 0 && /*#__PURE__*/ _jsxs("section", {
                className: "mb-8",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-xs uppercase tracking-wide text-muted-foreground mb-2",
                        children: "Folders"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2",
                        children: folders.map((f)=>/*#__PURE__*/ _jsxs("li", {
                                className: "group flex items-center gap-2 rounded-md border pr-1 hover:bg-muted",
                                children: [
                                    /*#__PURE__*/ _jsxs(Link, {
                                        to: `/editor/folder/${f.id}`,
                                        className: "flex items-center gap-2 px-3 py-2 flex-1 min-w-0",
                                        children: [
                                            /*#__PURE__*/ _jsx(FolderOpen, {
                                                className: "w-5 h-5 text-muted-foreground shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "truncate",
                                                children: f.name
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                                        children: [
                                            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                                asChild: true,
                                                children: /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    "aria-label": `Actions for folder ${f.name}`,
                                                    className: "rounded-md p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-accent",
                                                    children: /*#__PURE__*/ _jsx(MoreVertical, {
                                                        className: "w-4 h-4 text-muted-foreground"
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                                align: "end",
                                                children: [
                                                    /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                        onSelect: ()=>setRenameFolderTarget(f),
                                                        children: "Rename"
                                                    }),
                                                    /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        onSelect: ()=>setDeleteFolderTarget(f),
                                                        className: "text-destructive focus:text-destructive",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Trash2, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            " Delete folder"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }, f.id))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-baseline justify-between mb-2",
                        children: [
                            /*#__PURE__*/ _jsxs("h2", {
                                className: "text-xs uppercase tracking-wide text-muted-foreground",
                                children: [
                                    "Documents ",
                                    docData && !search ? `(${docData.total})` : ''
                                ]
                            }),
                            docsFetching && !docsLoading && /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-muted-foreground animate-pulse",
                                children: "Updating…"
                            })
                        ]
                    }),
                    docsLoading ? /*#__PURE__*/ _jsx("ul", {
                        className: "divide-y rounded-md border",
                        children: [
                            0,
                            1,
                            2
                        ].map((i)=>/*#__PURE__*/ _jsx(DocumentRowSkeleton, {}, i))
                    }) : noResults && search ? /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-md border px-6 py-10 text-center text-sm text-muted-foreground",
                        children: [
                            "No documents match ",
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-medium text-foreground",
                                children: [
                                    "“",
                                    search,
                                    "”"
                                ]
                            }),
                            "."
                        ]
                    }) : noResults ? /*#__PURE__*/ _jsx("div", {
                        className: "rounded-md border-dashed border-2 px-6 py-12 text-center",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "This folder is empty. Create a blank document or upload one to get started."
                        })
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "divide-y rounded-md border",
                        children: (docData?.documents ?? []).map((d)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex items-center gap-3 pr-2 hover:bg-muted",
                                children: [
                                    /*#__PURE__*/ _jsxs(Link, {
                                        to: `/editor/${d.id}`,
                                        className: "flex items-center gap-3 px-4 py-3 flex-1 min-w-0",
                                        children: [
                                            typeIcon(d.documentType),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex-1 min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "truncate font-medium",
                                                        children: d.name
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: [
                                                            "v",
                                                            d.versionCount,
                                                            " · ",
                                                            formatBytes(d.sizeBytes),
                                                            " · ",
                                                            relativeTime(d.updatedAt)
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                                        children: [
                                            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                                asChild: true,
                                                children: /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    "aria-label": `Actions for ${d.name}`,
                                                    className: "rounded-md p-2 hover:bg-accent",
                                                    children: /*#__PURE__*/ _jsx(MoreVertical, {
                                                        className: "w-4 h-4 text-muted-foreground"
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                                align: "end",
                                                children: [
                                                    /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                        onSelect: ()=>setRenameTarget(d),
                                                        children: "Rename"
                                                    }),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        onSelect: ()=>setMoveTarget(d),
                                                        children: [
                                                            /*#__PURE__*/ _jsx(ChevronRight, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            " Move to folder"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        onSelect: ()=>setDeleteTarget(d),
                                                        className: "text-destructive focus:text-destructive",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Trash2, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            " Delete"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }, d.id))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PromptDialog, {
                open: newFolderOpen,
                onOpenChange: setNewFolderOpen,
                title: "New folder",
                label: "Folder name",
                placeholder: "Project files",
                confirmLabel: "Create folder",
                onConfirm: async (name)=>{
                    await createFolder.mutateAsync({
                        name,
                        parentId: folderId
                    });
                    toast.success(`Created folder “${name}”`);
                }
            }),
            /*#__PURE__*/ _jsx(PromptDialog, {
                open: !!renameFolderTarget,
                onOpenChange: (open)=>!open && setRenameFolderTarget(null),
                title: "Rename folder",
                label: "Folder name",
                initialValue: renameFolderTarget?.name ?? '',
                confirmLabel: "Rename",
                onConfirm: async (name)=>{
                    if (!renameFolderTarget) return;
                    await renameFolder.mutateAsync({
                        id: renameFolderTarget.id,
                        name
                    });
                    toast.success('Folder renamed');
                }
            }),
            /*#__PURE__*/ _jsx(ConfirmDialog, {
                open: !!deleteFolderTarget,
                onOpenChange: (open)=>!open && setDeleteFolderTarget(null),
                title: "Delete folder?",
                description: `“${deleteFolderTarget?.name ?? ''}” will be deleted. Documents inside it will move to the root.`,
                confirmLabel: "Delete folder",
                destructive: true,
                onConfirm: async ()=>{
                    if (!deleteFolderTarget) return;
                    await deleteFolder.mutateAsync(deleteFolderTarget.id);
                    toast.success('Folder deleted');
                }
            }),
            /*#__PURE__*/ _jsx(PromptDialog, {
                open: !!renameTarget,
                onOpenChange: (open)=>!open && setRenameTarget(null),
                title: "Rename document",
                label: "Document name",
                initialValue: renameTarget?.name ?? '',
                confirmLabel: "Rename",
                onConfirm: async (name)=>{
                    if (!renameTarget) return;
                    await renameDoc.mutateAsync({
                        id: renameTarget.id,
                        name
                    });
                    toast.success('Renamed');
                }
            }),
            moveTarget && /*#__PURE__*/ _jsx(MoveDialog, {
                open: !!moveTarget,
                onOpenChange: (open)=>!open && setMoveTarget(null),
                documentName: moveTarget.name,
                currentFolderId: moveTarget.folderId,
                onMove: async (destination)=>{
                    await moveDoc.mutateAsync({
                        id: moveTarget.id,
                        folderId: destination
                    });
                    toast.success('Moved');
                }
            }),
            /*#__PURE__*/ _jsx(ConfirmDialog, {
                open: !!deleteTarget,
                onOpenChange: (open)=>!open && setDeleteTarget(null),
                title: "Delete document?",
                description: `“${deleteTarget?.name ?? ''}” will be moved to trash. You can ask an admin to restore it within 30 days.`,
                confirmLabel: "Delete",
                destructive: true,
                onConfirm: async ()=>{
                    if (!deleteTarget) return;
                    await deleteDoc.mutateAsync(deleteTarget.id);
                    toast.success('Deleted');
                }
            })
        ]
    });
}
